/* eslint-disable no-unused-vars */

import React, { useState, useEffect } from "react";
import {
  XIcon,
  HeartIcon,
  SearchIcon,
  UserAddIcon,
  StarIcon,
} from "@heroicons/react/outline";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/solid";
import { favoritesService } from "../../services/FavoritesService";
import { useAuth } from "../../context/AuthContext";

const AddVendorModal = ({ isOpen, onClose, onAddVendor, favorites = [] }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("favorites");
  const [searchTerm, setSearchTerm] = useState("");
  const [manualVendor, setManualVendor] = useState({
    businessName: "",
    businessEmail: "",
    businessPhone: "",
    businessType: "",
  });

  if (!isOpen) return null;

  const filteredFavorites = favorites.filter(
    (vendor) =>
      vendor.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.businessType?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleManualAdd = () => {
    if (!manualVendor.businessName || !manualVendor.businessEmail) {
      alert("Please fill in at least business name and email");
      return;
    }
    onAddVendor(manualVendor);
    setManualVendor({
      businessName: "",
      businessEmail: "",
      businessPhone: "",
      businessType: "",
    });
    onClose();
  };

  const handleQuickAdd = (vendor) => {
    onAddVendor({
      businessName: vendor.businessName,
      businessEmail: vendor.email,
      businessPhone: vendor.phone,
      businessType: vendor.businessType,
      isFavorite: true,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Add Vendor</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab("favorites")}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2 ${
                activeTab === "favorites"
                  ? "border-green-500 text-green-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <HeartSolidIcon className="w-4 h-4" />
              Favorites ({favorites.length})
            </button>
            <button
              onClick={() => setActiveTab("manual")}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2 ${
                activeTab === "manual"
                  ? "border-green-500 text-green-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <UserAddIcon className="w-4 h-4" />
              Manual Add
            </button>
          </nav>
        </div>

        <div
          className="p-4 overflow-y-auto"
          style={{ maxHeight: "calc(85vh - 180px)" }}
        >
          {activeTab === "favorites" ? (
            <div className="space-y-4">
              {favorites.length > 0 && (
                <div className="relative">
                  <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search favorites..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              )}

              {filteredFavorites.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <HeartIcon className="w-6 h-6 text-gray-400" />
                  </div>
                  <h3 className="font-medium text-gray-900 mb-1">
                    {favorites.length === 0
                      ? "No favorites yet"
                      : "No matches found"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {favorites.length === 0
                      ? "Mark vendors as favorites from their profile to quick-add them here"
                      : "Try a different search term"}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredFavorites.map((vendor, idx) => (
                    <div
                      key={vendor.mpsId || idx}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <StarIcon className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {vendor.businessName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {vendor.businessType}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleQuickAdd(vendor)}
                        className="px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Manually add a vendor by entering their details below.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Name *
                </label>
                <input
                  type="text"
                  value={manualVendor.businessName}
                  onChange={(e) =>
                    setManualVendor((prev) => ({
                      ...prev,
                      businessName: e.target.value,
                    }))
                  }
                  placeholder="Enter business name"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={manualVendor.businessEmail}
                  onChange={(e) =>
                    setManualVendor((prev) => ({
                      ...prev,
                      businessEmail: e.target.value,
                    }))
                  }
                  placeholder="vendor@email.com"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={manualVendor.businessPhone}
                  onChange={(e) =>
                    setManualVendor((prev) => ({
                      ...prev,
                      businessPhone: e.target.value,
                    }))
                  }
                  placeholder="+1 234 567 8910"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Type
                </label>
                <select
                  value={manualVendor.businessType}
                  onChange={(e) =>
                    setManualVendor((prev) => ({
                      ...prev,
                      businessType: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select type</option>
                  <option value="Food & Beverage">Food & Beverage</option>
                  <option value="Art & Craft">Art & Craft</option>
                  <option value="Clothing & Fashion">Clothing & Fashion</option>
                  <option value="Jewelry">Jewelry</option>
                  <option value="Services">Services</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <button
                onClick={handleManualAdd}
                className="w-full py-2.5 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors"
              >
                Add Vendor
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddVendorModal;
